import type { JSX } from 'react';
import { Box, styled } from '@mui/material';
import { CornerDownRightIcon } from 'lucide-react';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import {
  POPUP_GLYPH_INDENT,
  POPUP_TEXT_SIZE,
  TIMELINE_COLOURS,
} from '@/constants/PatientDashboard/patientTimeline';
import type { ITimelinePopupSampleEntry } from '@/types/PatientDashboard/PatientTimeline.types';
import SampleMark from './SampleMark';
import TimelinePopupBadge from './TimelinePopupBadge';

const NEST_ICON_SIZE = 8;

const RowRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const NameRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
});

const sampleTextSx = {
  fontSize: POPUP_TEXT_SIZE,
  lineHeight: '140%',
  fontWeight: 500,
} as const;

const sampleSubTextSx = {
  fontSize: '12px',
  lineHeight: '130%',
  fontWeight: 400,
} as const;

interface IProps {
  sample: ITimelinePopupSampleEntry;
}

export default function TimelinePopupSampleRow({ sample }: IProps): JSX.Element {
  return (
    <RowRoot style={{ paddingLeft: sample.isChild ? POPUP_GLYPH_INDENT : undefined }}>
      <NameRow>
        {sample.isChild ? (
          <CornerDownRightIcon
            size={NEST_ICON_SIZE}
            color={corePalette.grey100}
            aria-label="Derived sample"
          />
        ) : (
          <SampleMark
            colour={TIMELINE_COLOURS.sampleDot}
            isLiquidBiopsy={sample.isLiquidBiopsy}
            isParent
            index={0}
          />
        )}
        <CustomTypography
          truncate
          fontWeight="medium"
          sx={{ ...sampleTextSx, color: corePalette.grey200 }}
        >
          {sample.name}
        </CustomTypography>
        {sample.assay && <TimelinePopupBadge label={sample.assay} />}
      </NameRow>
      <CustomTypography
        sx={{ ...sampleSubTextSx, pl: POPUP_GLYPH_INDENT, color: corePalette.grey100 }}
      >
        {`${sample.specimenPurposeAbbreviation} | ${sample.subtype}`}
      </CustomTypography>
    </RowRoot>
  );
}
